import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import clsx from 'clsx';

export type Variant = 'primary' | 'accent' | 'secondary' | 'danger' | 'ghost' | 'success' | 'warning';
export type Size = 'sm' | 'md' | 'lg';

const base =
  'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const sizes: Record<Size, string> = { sm: 'px-2.5 py-1.5 text-xs', md: 'px-3.5 py-2 text-sm', lg: 'px-5 py-3 text-base' };
const variants: Record<Variant, string> = {
  primary: 'bg-brand-500 text-ink-900 shadow-sm hover:bg-brand-400',
  accent: 'bg-accent-600 text-white shadow-sm hover:bg-accent-700',
  secondary: 'border border-slate-300 bg-white text-slate-700 shadow-sm hover:bg-slate-50 dark:border-ink-600 dark:bg-ink-800 dark:text-slate-200 dark:hover:bg-ink-700',
  danger: 'bg-rose-600 text-white shadow-sm hover:bg-rose-700',
  ghost: 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-ink-800',
  success: 'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700',
  warning: 'bg-amber-500 text-ink-900 shadow-sm hover:bg-amber-400',
};

/** Class string for anything that should look like a button (links, labels). */
export const buttonClass = (variant: Variant = 'primary', size: Size = 'md', extra?: string) => clsx(base, sizes[size], variants[variant], extra);

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', loading, disabled, className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <button ref={ref} type={type} disabled={disabled || loading} aria-busy={loading || undefined} className={buttonClass(variant, size, className)} {...rest}>
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});

export function LinkButton({ variant = 'primary', size = 'md', className, ...rest }: LinkProps & { variant?: Variant; size?: Size }) {
  return <Link className={buttonClass(variant, size, className)} {...rest} />;
}
